import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { leaveService } from "../../api/leaveService";
import Badge from "../../components/ui/Badge";
import ApprovalProgress from "../../components/ui/ApprovalProgress";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Tabs from "../../components/ui/Tabs";
import { useToast } from "../../components/ui/useToast";
import { getErrorMessage } from "../../utils/apiError";
import { formatManilaDate, todayManilaKey } from "../../utils/manilaTime";
import { getEmployeeId } from "../../utils/session";
import { cx } from "../../utils/classNames";
import {
  canCancelLeave,
  formatDateRange,
  getLeaveYearOptions,
  LEAVE_STATUS,
  MY_LEAVE_BALANCES_KEY,
  MY_LEAVE_REQUESTS_KEY,
  STATUS_FILTER_TABS,
} from "./leaveUtils";
import FileLeaveModal from "./FileLeaveModal";
import ui from "../../components/ui/ui.module.css";
import styles from "./MyLeaves.module.css";

const COLUMN_COUNT = 8;
const toArray = (data) => (Array.isArray(data) ? data : []);

const MyLeavesView = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const hasEmployee = !!getEmployeeId();

  const [year, setYear] = useState(() => new Date().getFullYear());
  const [statusTab, setStatusTab] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFormOpen, setIsFormOpen] = useState(
    () => searchParams.get("file") === "1",
  );
  const [cancelTarget, setCancelTarget] = useState(null);

  const status = statusTab === "all" ? "" : statusTab;
  const yearFilters = { status: "", year };
  const tabFilters = { status, year };

  const balancesQuery = useQuery({
    queryKey: [MY_LEAVE_BALANCES_KEY, year],
    queryFn: () => leaveService.getMyBalances({ year }),
    enabled: hasEmployee,
  });

  // All of the year's requests: used by the form's overlap check.
  const yearRequestsQuery = useQuery({
    queryKey: [MY_LEAVE_REQUESTS_KEY, yearFilters],
    queryFn: () => leaveService.getMyRequests(yearFilters),
    enabled: hasEmployee,
  });

  const requestsQuery = useQuery({
    queryKey: [MY_LEAVE_REQUESTS_KEY, tabFilters],
    queryFn: () => leaveService.getMyRequests(tabFilters),
    enabled: hasEmployee,
    placeholderData: keepPreviousData,
  });

  const balances = toArray(balancesQuery.data);
  const requests = toArray(requestsQuery.data);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: [MY_LEAVE_BALANCES_KEY] });
    queryClient.invalidateQueries({ queryKey: [MY_LEAVE_REQUESTS_KEY] });
  };

  const cancelMutation = useMutation({
    mutationFn: (id) => leaveService.cancelMyLeave(id),
    onSuccess: () => toast.success("Leave request cancelled."),
    onError: (error) =>
      toast.error(getErrorMessage(error, "Unable to cancel this leave.")),
    onSettled: () => {
      setCancelTarget(null);
      refresh();
    },
  });

  if (!hasEmployee) {
    return (
      <div className={cx(ui.alert, ui.alertDanger)}>
        Your account is not linked to an employee record.
      </div>
    );
  }

  const today = todayManilaKey();
  const message = requestsQuery.isLoading
    ? "Loading requests…"
    : requestsQuery.isError
      ? getErrorMessage(
          requestsQuery.error,
          "Unable to load your leave requests.",
        )
      : requests.length === 0
        ? "No leave requests."
        : "";

  return (
    <div className={ui.page}>
      <div className={ui.pageHeader}>
        <div>
          <h4 className="page-title">My Leaves</h4>
          <p className="page-subtitle">
            Your leave balances, requests and approval status.
          </p>
        </div>
        <div className={ui.actions}>
          <select
            className={cx(ui.input, styles.yearSelect)}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            aria-label="Year"
          >
            {getLeaveYearOptions().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={cx(ui.button, ui.primary)}
            onClick={() => setIsFormOpen(true)}
          >
            <i className="bi bi-plus-lg"></i>
            File Leave
          </button>
        </div>
      </div>

      {/* BALANCES */}
      <div className={styles.balanceGrid}>
        {balancesQuery.isLoading && (
          <div className={cx(ui.card, ui.empty)}>Loading balances…</div>
        )}
        {balancesQuery.isError && (
          <div className={cx(ui.alert, ui.alertDanger)}>
            {getErrorMessage(
              balancesQuery.error,
              "Unable to load your leave balances.",
            )}
          </div>
        )}
        {balancesQuery.isSuccess && balances.length === 0 && (
          <div className={cx(ui.card, ui.empty)}>
            No leave balances for {year}.
          </div>
        )}
        {balances.map((b) => (
          <div
            key={b.id}
            className={cx(
              styles.balanceCard,
              b.allocated === 0 && styles.balanceEmpty,
            )}
          >
            <div className={styles.balanceType}>
              {b.leaveType?.name ?? "Leave"}
            </div>
            <div className={styles.balanceAvailable}>
              {b.available}
              <span className={styles.balanceUnit}> available</span>
            </div>
            <div className={styles.balanceMeta}>
              Allocated {b.allocated} · Used {b.used} · Pending {b.pending}
            </div>
          </div>
        ))}
      </div>

      {/* REQUESTS */}
      <div className={ui.card}>
        <Tabs
          tabs={STATUS_FILTER_TABS}
          active={statusTab}
          onChange={setStatusTab}
        />

        <div className={ui.tableWrapper}>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Dates</th>
                <th>Days</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Reviewer remarks</th>
                <th>Filed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {message && (
                <tr>
                  <td
                    colSpan={COLUMN_COUNT}
                    className={
                      requestsQuery.isError
                        ? "text-danger"
                        : cx(ui.muted, "text-center")
                    }
                  >
                    {message}
                  </td>
                </tr>
              )}

              {requests.map((request) => {
                const meta = LEAVE_STATUS[request.status] ?? {
                  label: request.status,
                  tone: "gray",
                };
                return (
                  <tr key={request.id}>
                    <td className={ui.strong}>
                      {request.leaveType?.name ?? "—"}
                    </td>
                    <td className={ui.noWrap}>
                      {formatDateRange(request.startDate, request.endDate)}
                    </td>
                    <td>{Number(request.totalDays)}</td>
                    <td className={ui.truncate} title={request.reason}>
                      {request.reason || "—"}
                    </td>
                    <td>
                      <Badge tone={meta.tone}>{meta.label}</Badge>
                      <ApprovalProgress
                        isPending={request.status === "pending"}
                        current={request.currentLevel ?? 1}
                        total={request.totalLevels ?? 1}
                        approvals={request.approvals ?? []}
                        waitingFor={request.waitingFor}
                        managersAbove={request.managersAbove}
                      />
                    </td>
                    <td>
                      <ReviewerRemarks request={request} />
                    </td>
                    <td className={cx(ui.noWrap, ui.muted)}>
                      {formatManilaDate(request.createdAt)}
                    </td>
                    <td className="text-end">
                      {canCancelLeave(request, today) && (
                        <button
                          type="button"
                          className={cx(
                            ui.button,
                            ui.dangerOutline,
                            ui.buttonSmall,
                          )}
                          onClick={() => setCancelTarget(request)}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {isFormOpen && (
        <FileLeaveModal
          year={year}
          balances={balances}
          existingRequests={toArray(yearRequestsQuery.data)}
          onClose={() => {
            setIsFormOpen(false);
            if (searchParams.has("file"))
              setSearchParams({}, { replace: true });
          }}
        />
      )}

      {cancelTarget && (
        <ConfirmDialog
          title="Cancel leave?"
          message={`Cancel your ${cancelTarget.leaveType?.name ?? "leave"} for ${formatDateRange(
            cancelTarget.startDate,
            cancelTarget.endDate,
          )}?`}
          confirmLabel="Cancel leave"
          tone="danger"
          isPending={cancelMutation.isPending}
          onConfirm={() => cancelMutation.mutate(cancelTarget.id)}
          onClose={() => setCancelTarget(null)}
        />
      )}
    </div>
  );
};

// Rejected: the reason, shown prominently. Approved: the approver's note.
const ReviewerRemarks = ({ request }) => {
  if (!request.reviewRemarks) return <span className={ui.muted}>—</span>;

  if (request.status === "rejected") {
    return (
      <span className={styles.rejectReason}>
        Reason: {request.reviewRemarks}
      </span>
    );
  }
  return <span className={styles.approverNote}>{request.reviewRemarks}</span>;
};

export default MyLeavesView;
